<template>
  <div>
    <b-dropdown
      class="overflow-menu-wrapper"
      variant="link"
      toggle-class="edit-menu-button edit-goal-menu-button text-dark text-decoration-none overflow-button"
      menu-class="overflow-menu"
      :lazy="true"
      no-caret
    >
      <template slot="button-content">
        <i class="overflow-menu-icon"></i>
      </template>
      <template v-if="goal.isArchived">
        <b-dropdown-item @click="triggerConfirmation('unarchive')">Restore Goal</b-dropdown-item>
        <b-dropdown-item @click="triggerConfirmation('delete')">Delete Goal</b-dropdown-item>
      </template>
      <template v-else>
        <b-dropdown-item @click="goToGoalPage">
          <action-key action="Open goal page" :key-combination="['Shift', '<right>']" />
        </b-dropdown-item>
        <b-dropdown-item @click="openEditGoal">
          Edit Goal & KRs
        </b-dropdown-item>
        <b-dropdown-item @click="isCreateGoalModalShown = true">
          <div class="d-flex justify-content-between align-items-center">
            Add a child Goal
            <i class="tree-node-icon flex-shrink-0"></i>
          </div>
        </b-dropdown-item>
        <b-dropdown-item @click="openParentGoalModal = true">
          <div class="d-flex justify-content-between align-items-center">
            Align to parent Goals
            <i class="tree-node-icon flex-shrink-0"></i>
          </div>
        </b-dropdown-item>
        <b-dropdown-item @click="isSetupGoalProgressModalShown = true">
          <div class="d-flex justify-content-between align-items-center">
            Goal contributions
            <i class="goal-progress-icon flex-shrink-0"></i>
          </div>
        </b-dropdown-item>
        <b-dropdown-divider />
        <b-dropdown-item @click="isBulkCheckinModalShown = true"
          >Check-in to all KRs
        </b-dropdown-item>
        <b-dropdown-item @click="isMoveGoalModalShown = true">Move Goal</b-dropdown-item>
        <b-dropdown-item @click="isCloneGoalShown = true">Clone Goal</b-dropdown-item>
        <b-dropdown-item @click="editGoal('settings')">All Goal settings</b-dropdown-item>
        <b-dropdown-divider />
        <follow-option :goal-id="goal.id" />
        <b-dropdown-divider />
        <all-initiatives-nav-option
          type="Goal"
          :goal="goal"
          :disabled="goal.keyResults.length === 0"
        />
        <b-dropdown-divider />
        <b-dropdown-item @click="$emit('show-award-modal')">
          <div class="d-flex justify-content-between align-items-center">
            <span>Give an award</span>
            <i class="award-icon options-menu-icon"></i>
          </div>
        </b-dropdown-item>
        <b-dropdown-item @click.stop="requestUpdate">Request an update</b-dropdown-item>
        <b-dropdown-divider />
        <b-dropdown-item @click="triggerConfirmation('reopen')" v-if="goal.isClosed"
          >Re-open Goal
        </b-dropdown-item>
        <b-dropdown-item @click="isCloseGoalModalShown = true" v-else>Close Goal</b-dropdown-item>
        <b-dropdown-item @click="triggerConfirmation('archive')">Archive Goal</b-dropdown-item>
      </template>
    </b-dropdown>
    <bulk-check-in
      v-if="isBulkCheckinModalShown"
      v-model="isBulkCheckinModalShown"
      :org-id="goal.organizationId"
      :source-goal-id="goal.id"
      :source="source"
    />
    <close-entity
      :entity="goal"
      v-if="isCloseGoalModalShown"
      v-model="isCloseGoalModalShown"
      @closed="$emit('close-goal')"
    />
    <clone-goal v-if="isCloneGoalShown" v-model="isCloneGoalShown" :goal-id="goal.id" />
    <confirm-dialog
      ref="confirmModal"
      :title="confirmationMessage.title"
      @ok="onConfirmation"
      :ok-title="confirmationMessage.ok"
      :ok-variant="action === 'delete' ? 'danger' : 'primary'"
      :cancel-title="confirmationMessage.cancel || 'No, I changed my mind'"
    >
      <span v-if="goal.type === 'team' && action === 'unarchive'">
        {{ confirmationMessage.bodySecondary }}
      </span>
      <span v-else>
        {{ confirmationMessage.body }}
      </span>
    </confirm-dialog>
    <parent-goals-alignment
      :org-id="goal.organizationId"
      :sub-org-id="subOrgId"
      :goal="goal"
      :save-on-submit="true"
      v-model="openParentGoalModal"
      v-if="openParentGoalModal"
    />
    <create-goal-modal
      v-model="isCreateGoalModalShown"
      :teamId="goal.teamId"
      :org-id="goal.organizationId"
      :parent-goal="goal"
      :goal-meta="{ timeline: goal.timeline }"
      title="Create a child goal"
      @created="showChildGoalCreationToast"
      v-if="isCreateGoalModalShown"
    />
    <setup-goal-progress-modal
      :goal-id="goal.id"
      :source="SOURCE.GOAL_MENU"
      v-model="isSetupGoalProgressModalShown"
      v-if="isSetupGoalProgressModalShown"
    />
    <move-goal-modal
      @move="$emit('move-goal')"
      :goal="goal"
      v-model="isMoveGoalModalShown"
      v-if="isMoveGoalModalShown"
    />
  </div>
</template>

<script>
import CloseEntity from '@/components/CloseEntity'
import CreateGoalModal from '@/components/goal/CreateGoalModal'
import FollowOption from '@/components/goal/FollowOption'
import MoveGoalModal from '@/components/goal/MoveGoalModal.vue'
import SetupGoalProgressModal from '@/components/goal/SetupGoalProgressModal'
import events from '@/constants/events'
import EventBusMixin from '@/mixins/EventBusMixin'
import ParentGoalsMixin from '@/mixins/ParentGoalsMixin'
import TeamMixin from '@/mixins/TeamMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import { getEditGoalPageRouteConfig, getGoalPageRouteConfig } from '@/utils/navigationConfigs'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import * as R from 'ramda'
import { BDropdown, BDropdownItem, BDropdownDivider } from 'bootstrap-vue'
import ActionKey from '@/components/ActionKey'
import BulkCheckIn from '@/components/BulkCheckIn'
import ConfirmDialog from '@/components/ConfirmDialog'
import CloneGoal from '@/components/CloneGoal'
import ParentGoalsAlignment from '@/components/goal/ParentGoalsAlignment'
import AllInitiativesNavOption from '@/components/goal/AllInitiativesNavOption'
import handleClickNavigation from '@/utils/handleClickNavigation'
import { DEFAULT_ERROR } from '@/constants/messages'
import errorCodes from '@/constants/errorCodes'
import EventBus from '@/EventBus'
import { TOAST_TYPE } from '@/constants/toasts'
import { mapActions } from 'vuex'
import { trackEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME, SOURCE } from '@/constants/analytics'

const messages = {
  delete: {
    title: 'Delete this Goal?',
    body:
      'Deleting this goal will delete all related KRs and associated Initiatives. This action cannot be undone.',
    ok: 'Yes, delete'
  },
  unarchive: {
    title: 'Restore this Goal?',
    body: "We'll move it back to the Org Goals list.",
    bodySecondary: 'We’ll move it to the same team it belonged to before it was archived.',
    ok: 'Yes, restore'
  },
  archive: {
    title: 'Are you sure you want to archive this goal?',
    body: 'You can always restore an archived goal',
    ok: 'Yes, archive'
  },
  forceUnarchive: {
    title: 'There was a problem',
    body: 'We need to restore the team to restore this goal',
    ok: 'Restore team and goal',
    cancel: 'Cancel'
  },
  reopen: {
    title: 'Reopen this Goal?',
    body: 'Are you sure you want to reopen this goal.',
    ok: 'Yes, re-open'
  }
}

/**
 * This is the component for Goal Overflow Dropdown Menu
 * @emits
 * - delete-goal
 * - archive-goal
 * - restore-goal
 * - close-goal
 * - reopen-goal
 * - show-award-modal
 */
export default {
  name: 'GoalOptions',
  props: ['goal', 'source'],
  mixins: [TeamMixin, EventBusMixin, ParentGoalsMixin],
  components: {
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    ActionKey,
    BulkCheckIn,
    ConfirmDialog,
    CloneGoal,
    AllInitiativesNavOption,
    ParentGoalsAlignment,
    CloseEntity,
    FollowOption,
    CreateGoalModal,
    SetupGoalProgressModal,
    MoveGoalModal
  },
  data() {
    return {
      isBulkCheckinModalShown: false,
      isSetupGoalProgressModalShown: false,
      messages,
      confirmationMessage: {},
      action: null,
      isCloneGoalShown: false,
      openParentGoalModal: false,
      isCloseGoalModalShown: false,
      isCreateGoalModalShown: false,
      isMoveGoalModalShown: false,
      SOURCE
    }
  },
  computed: {
    owners() {
      return this.goal.ownerships.map(x => x.userId)
    },
    subOrgId() {
      return this.getClosestOrgLikeTeamId(this.goal.teamId)
    }
  },
  methods: {
    ...mapActions(['archiveGoal', 'getCurrentUser']),
    addAnalytics(sectionToFocus) {
      if (sectionToFocus === 'align') {
        trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.CHANGE_ALIGNMENT, EVENT_NAME.GOAL_MENU)
      }
    },
    editGoal(sectionToFocus, action) {
      let query = {}
      if (sectionToFocus) {
        query.section = sectionToFocus
      }
      if (action) {
        query.action = action
      }
      const location = getEditGoalPageRouteConfig({
        orgId: this.goal.organizationId,
        goalId: this.goal.id,
        query
      })
      this.$router.push(location)
      this.addAnalytics(sectionToFocus)
    },
    openEditGoal() {
      this.editGoal()
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.EDIT_GOAL, EVENT_NAME.GOAL_MENU)
    },
    goToGoalPage(event) {
      const location = getGoalPageRouteConfig({
        orgId: this.goal.organizationId,
        goalId: this.goal.id
      })
      handleClickNavigation.call(this, event, location)
    },
    async deleteGoal() {
      try {
        await this.$store.dispatch('deleteGoal', this.goal)
        this.$emit('delete-goal')
      } catch (e) {
        const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      }
    },
    async restoreGoal(force) {
      const { errorCode, success } = await this.archive('unarchive', force)
      if (success) {
        this.$emit('unarchive-goal')
        if (force) {
          this.getCurrentUser()
        }
      } else if (errorCode === errorCodes.teamArchived) {
        const team = this.$store.getters.teamById(this.goal.teamId)
        this.messages.forceUnarchive.body = `The team (${
          team.name
        }) this goal belongs to has been archived. We need to restore the team first to restore this goal.`
        this.triggerConfirmation('forceUnarchive')
      }
    },
    async archive(action = 'archive', force) {
      try {
        await this.archiveGoal({
          action,
          id: this.goal.id,
          organizationId: this.goal.organizationId,
          force
        })
        EventBus.$emit('ADD_TOAST', {
          message: `Goal has been ${action === 'archive' ? 'archived' : 'restored'}`
        })
        if (this.action === 'archive') {
          this.refreshParentGoals(this.goal.id) // Did this to make sure that UI is updated with progress of all parent goals
        }
        return { success: true }
      } catch (e) {
        const errorCode = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'code'], e)
        if (errorCode) {
          return { errorCode }
        } else {
          const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
          EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
        }
      }
    },
    async archiveCurrentGoal() {
      const { success: isArchived } = await this.archive('archive')
      if (isArchived) {
        this.$emit('archive-goal')
      }
    },
    async requestUpdate() {
      const params = {
        orgId: this.goal.organizationId,
        goalId: this.goal.id
      }
      try {
        await this.$store.dispatch('requestGoalUpdate', params)
        EventBus.$emit('ADD_TOAST', { message: 'Your update request is sent to all owners' })
        trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.REQUEST_UPDATE_MENU, EVENT_NAME.GOAL)
      } catch (e) {
        const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      }
    },
    triggerConfirmation(action) {
      this.action = action
      this.confirmationMessage = messages[action]
      this.$refs.confirmModal.$refs.dialog.show()
    },
    async performSelectedAction() {
      try {
        const { id: goalId, organizationId: orgId } = this.goal
        await service.client.post(urlManager.goalDetail({ orgId, goalId }), { action: this.action })
        this.$store.dispatch('getGoal', { goalId, orgId })
        showToast('Goal has been re-opened successfully')
      } catch (e) {
        showAPIError(e)
        throw e
      }
    },
    async onConfirmation() {
      if (this.action === 'archive') {
        this.archiveCurrentGoal()
      } else if (this.action === 'unarchive') {
        this.restoreGoal()
      } else if (this.action === 'delete') {
        this.deleteGoal()
      } else if (this.action === 'forceUnarchive') {
        this.restoreGoal(true)
      } else if (this.action === 'reopen') {
        await this.performSelectedAction()
        this.$emit('reopen-goal')
      }
    },
    showChildGoalCreationToast({ parentIds }) {
      showToast('Goal has been created successfully')
      if (parentIds?.length > 0) {
        this.eventBusEmit(events.CHILD_GOAL_CREATED, { parentIds })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_optionsMenu.scss';

/deep/ .overflow-menu {
  width: 230px;
}

.award-icon {
  @include dimension(20px, 20px);
  background-size: contain;
}
</style>
